import { Field, useFormikContext } from 'formik';
import { useCallback } from 'react';

import {
  ADDITIONAL_INFORMATION_STATUS,
  AdditionalInformation,
} from '@hivebrite/design-system/AdditionalInformation';
import { Box } from '@hivebrite/design-system/Box';

import { DEFAULT_INPUT_NAME } from 'hb-react/shared/components/Turnstile/constants';

import { TurnstileWidget } from './TurnstileWidget';

interface TurnstileFormikFieldProps {
  name?: string;
  onError?: (error: string) => void;
  onExpire?: () => void;
  onSuccess?: (token: string) => void;
}

/**
 * A component that wraps the TurnstileWidget and store the token in a formik Field.
 * If you are not using Formik, prefer using the TurnstileInput component.
 *
 * All props are optional. `name` will default to the value of DEFAULT_INPUT_NAME and
 * the callbacks are there for convenience only as the token will be stored in the hidden input internally
 * and turnstile will retry the challenge automatically. on its own.
 *
 * @param name The name of the hidden input that will store the token.
 * @param onSuccess A callback that will be called when the Turnstile challenge is successfully resolved.
 * @param onError A callback that will be called when an error occurs during the Turnstile challenge.
 * @param onExpire A callback that will be called when the Turnstile challenge expires.
 */
export const TurnstileFormikField = ({
  name = DEFAULT_INPUT_NAME,
  onError,
  onExpire,
  onSuccess,
}: TurnstileFormikFieldProps) => {
  const { setFieldValue, submitCount } = useFormikContext();

  const handleSuccess = useCallback(
    (token) => {
      setFieldValue(name, token, true);

      onSuccess?.(token);
    },
    [name, onSuccess, setFieldValue],
  );

  return (
    <Field
      name={name}
      validate={(value) => {
        if (!value) {
          return __('Required');
        }
      }}
    >
      {({ meta }) => (
        <Box flexDirection="column">
          <TurnstileWidget
            onSuccess={handleSuccess}
            onError={onError}
            onExpire={onExpire}
          />
          {!!meta.error && submitCount > 0 && (
            <AdditionalInformation
              id={`${name}-error`}
              status={ADDITIONAL_INFORMATION_STATUS.ERROR}
            >
              {meta.error}
            </AdditionalInformation>
          )}
        </Box>
      )}
    </Field>
  );
};
